/*━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/

/**
 * gsapSlider.js
 *
 * GSAPを使用したカスタムスライダーアニメーション
 *
 */

/*━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/

/**
 * 初期設定
 */
/*━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
import gsap from "gsap";

const setGsapFlip = () => {
	// スライダーのコンテナ要素を取得
	const slider = document.querySelector(".slider-concept8 .slider-wrapper");
	if (!slider) {
		return; // スライダーが存在しない場合は処理を中断
	}

	// スライダーの設定
	const config = {
		duration: 0.9, // アニメーション時間
		ease: "sine.inOut", // イージング関数
		autoplaySpeed: 4000, // オートプレイの間隔（ミリ秒）
		isPlaying: true, // オートプレイの状態
		visibleSlides: 8, // 表示するスライドの最大数（PC）
		// レスポンシブ設定
		responsive: {
			mobile: {
				breakpoint: 767, // スマートフォンのブレイクポイント
				gap: 40, // スマートフォン用のギャップ（px）
				visibleSlides: 4, // スマートフォンで表示するスライド数
			},
			tablet: {
				breakpoint: 1024, // タブレットのブレイクポイント
				gap: 45, // タブレット用のギャップ（px）
				visibleSlides: 8, // 表示するスライドの最大数（PC）
			},
			desktop: {
				breakpoint: 1440, // デスクトップのブレイクポイント
				gap: 60, // スライド間のギャップ（px）
				visibleSlides: 8, // 表示するスライドの最大数（PC）
			},
		},
		gap: "4%", // 1440px以上のスライド間のギャップ（%）
	};

	// 画面サイズに応じた設定を適用
	function applyResponsiveConfig() {
		// デフォルト（PC）設定を使用
		let currentGap = config.gap;
		let currentVisibleSlides = config.visibleSlides;

		// 画面幅を取得
		const windowWidth = window.innerWidth;

		// パーセンテージをピクセルに変換する関数
		const convertPercentToPx = (percent, containerWidth) => {
			return (containerWidth * parseFloat(percent)) / 100;
		};

		// スマートフォンの設定
		if (windowWidth <= config.responsive.mobile.breakpoint) {
			currentGap = config.responsive.mobile.gap;
			currentVisibleSlides = config.responsive.mobile.visibleSlides;
		}
		// タブレットの設定
		else if (windowWidth <= config.responsive.tablet.breakpoint) {
			currentGap = config.responsive.tablet.gap;
			currentVisibleSlides = config.responsive.tablet.visibleSlides;
		}
		// デスクトップの設定
		else if (windowWidth <= config.responsive.desktop.breakpoint) {
			currentGap = config.responsive.desktop.gap;
			currentVisibleSlides = config.responsive.desktop.visibleSlides;
		}
		// 1440px以上の設定
		else {
			// パーセンテージをピクセルに変換
			currentGap = convertPercentToPx(config.gap, windowWidth);
			currentVisibleSlides = config.responsive.desktop.visibleSlides;
		}

		// 設定を更新
		config.currentGap = currentGap;
		config.currentVisibleSlides = currentVisibleSlides;

		// スライダーを再初期化
		initSlider();
	}

	// ウィンドウのリサイズイベントに対応
	window.addEventListener("resize", function () {
		// リサイズ終了後に1回だけ実行するための遅延処理
		clearTimeout(config.resizeTimer);
		config.resizeTimer = setTimeout(function () {
			applyResponsiveConfig();
		}, 250);
	});

	// すべてのスライド要素を配列として取得
	const slides = gsap.utils.toArray(".slide");
	let currentIndex = 0; // 現在のスライドインデックス

	// スライダーの初期設定
	function initSlider() {
		// 各スライドの初期位置を設定
		slides.forEach((slide, index) => {
			// すべてのクラスをリセット
			slide.classList.remove("active-slide", "next-slide", "next-next-slide");

			if (index === 0) {
				// アクティブなスライド（最初のスライド）
				gsap.set(slide, {
					autoAlpha: 1,
					position: "relative",
					left: 0,
				});
				slide.classList.add("active-slide"); // アクティブスライドのクラスを追加
			} else if (index < config.currentVisibleSlides) {
				// 表示する次のスライド（visibleSlides数まで）
				gsap.set(slide, {
					autoAlpha: 1,
					position: "absolute",
					top: 0,
					left: `${index * config.currentGap}px`, // 現在のギャップでずらして配置
				});
				slide.classList.add("next-slide"); // 次のスライドのクラスを追加
			} else {
				// 表示範囲外のスライドは非表示
				gsap.set(slide, {
					autoAlpha: 0,
					position: "absolute",
					top: 0,
					left: "100%",
				});
			}
		});
	}

	// 次のスライドに進む関数
	function nextSlide() {
		// 現在のスライドインデックスを更新
		const prevIndex = currentIndex;
		currentIndex = (currentIndex + 1) % slides.length;

		// すべてのスライドを処理
		slides.forEach((slide, index) => {
			// 相対インデックスを計算（現在のスライドからの距離）
			let relativeIndex = (index - currentIndex + slides.length) % slides.length;

			// クラスをリセット
			slide.classList.remove("active-slide", "next-slide", "next-next-slide");

			if (relativeIndex === 0) {
				// 新しいアクティブスライド
				slide.classList.add("active-slide");
				gsap.to(slide, {
					left: 0,
					autoAlpha: 1,
					position: "relative",
					duration: config.duration,
					ease: config.ease,
				});
			} else if (relativeIndex < config.currentVisibleSlides) {
				// 表示する次のスライド
				slide.classList.add("next-slide");
				gsap.to(slide, {
					left: `${relativeIndex * config.currentGap}px`, // 現在のギャップでずらして配置
					autoAlpha: 1,
					position: "absolute",
					duration: config.duration,
					ease: config.ease,
				});
			} else {
				// 表示範囲外のスライド
				gsap.to(slide, {
					left: "100%",
					autoAlpha: 0,
					position: "absolute",
					duration: config.duration,
					ease: config.ease,
				});
			}
		});

		// 前のアクティブスライドを左にスライドアウト
		if (prevIndex !== currentIndex) {
			const prevSlide = slides[prevIndex];
			gsap.to(prevSlide, {
				left: "-100%",
				autoAlpha: 0,
				duration: config.duration,
				ease: config.ease,
			});
		}
	}

	// オートプレイの開始
	let autoplay;
	function startAutoplay() {
		if (config.isPlaying) {
			config.isPlaying = true;
			autoplay = setInterval(nextSlide, config.autoplaySpeed);
		}
	}

	// オートプレイの停止
	function stopAutoplay() {
		config.isPlaying = false;
		clearInterval(autoplay);
	}

	// 初期レスポンシブ設定の適用
	applyResponsiveConfig();

	// オートプレイを開始
	startAutoplay();

	// 次のスライドボタンのクリックイベントを設定
	const nextButton = document.querySelector(".slider-arrow-next");
	if (nextButton) {
		nextButton.addEventListener("click", () => {
			stopAutoplay(); // 一時的にオートプレイを停止
			nextSlide(); // 次のスライドへ
			startAutoplay(); // オートプレイを再開
		});
	}

	// マウスオーバーでオートプレイを一時停止
	slider.addEventListener("mouseover", stopAutoplay);

	// マウスアウトでオートプレイを再開
	slider.addEventListener("mouseout", startAutoplay);
};

/**
 * Export.
 */
/*━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
export { setGsapFlip };
